import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const partTypes = ['Outsourced', 'In-house', 'Both', 'Raw Material', 'Consumable', 'Finished Good'];

export default function PartsMaster() {
  const [assemblies, setAssemblies] = useState([]);
  const [flatParts, setFlatParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partVendors, setPartVendors] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('vendors');
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'flat'
  const [expandedAssemblies, setExpandedAssemblies] = useState({});

  const [partForm, setPartForm] = useState({
    partcode: '',
    description: '',
    part_type: 'Outsourced',
    category: '',
    unit: 'pieces',
    hsn_code: '',
    notes: ''
  });

  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = async () => {
    try {
      const [groupedRes, flatRes] = await Promise.all([
        axios.get(`${API}/parts-grouped`),
        axios.get(`${API}/parts`)
      ]);
      setAssemblies(groupedRes.data);
      setFlatParts(flatRes.data);
    } catch (error) {
      console.error('Error fetching parts:', error);
      setAssemblies([]);
      setFlatParts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartVendors = async (partcode, description) => {
    try {
      // Use encodeURIComponent for special characters
      const response = await axios.get(`${API}/parts/${encodeURIComponent(partcode)}/vendor-prices`);
      // Filter by description if provided
      const filtered = description 
        ? response.data.filter(v => v.description === description)
        : response.data;
      setPartVendors(filtered.length > 0 ? filtered : response.data);
    } catch (error) {
      console.error('Error fetching part vendors:', error);
      setPartVendors([]);
    }
  };

  const fetchPriceHistory = async (partcode, description) => {
    try {
      const response = await axios.get(`${API}/parts/${encodeURIComponent(partcode)}/price-history`);
      // Filter by description if provided (through vendor items)
      setPriceHistory(response.data);
    } catch (error) {
      console.error('Error fetching price history:', error);
      setPriceHistory([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate HSN code is provided
    if (!partForm.hsn_code || partForm.hsn_code.trim() === '') {
      alert('HSN/SAC Code is mandatory');
      return;
    }
    
    try {
      await axios.post(`${API}/parts`, partForm);
      setShowCreateModal(false);
      resetForm();
      fetchParts();
    } catch (error) {
      console.error('Error creating part:', error);
      alert('Error creating part: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (partId) => {
    if (window.confirm('Are you sure you want to delete this part?')) {
      try {
        await axios.delete(`${API}/parts/${partId}`);
        fetchParts();
        if (showDetailsModal) {
          setShowDetailsModal(false);
        }
      } catch (error) {
        console.error('Error deleting part:', error);
        alert('Error deleting part: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const viewPartDetails = async (part) => {
    setSelectedPart(part);
    setActiveTab('vendors');
    await fetchPartVendors(part.partcode, part.description);
    await fetchPriceHistory(part.partcode, part.description);
    setShowDetailsModal(true);
  };

  const resetForm = () => {
    setPartForm({
      partcode: '',
      description: '',
      part_type: 'Outsourced',
      category: '',
      unit: 'pieces',
      hsn_code: '',
      notes: ''
    });
  };

  const toggleAssembly = (assemblyCode) => {
    setExpandedAssemblies(prev => ({
      ...prev,
      [assemblyCode]: !prev[assemblyCode]
    }));
  };

  const expandAll = () => {
    const allExpanded = {};
    assemblies.forEach(a => { allExpanded[a.assembly_code] = true; });
    setExpandedAssemblies(allExpanded);
  };

  const collapseAll = () => {
    setExpandedAssemblies({});
  };

  // Filter assemblies
  const filteredAssemblies = assemblies.filter(assembly => {
    const matchesType = !filterType || assembly.part_types.includes(filterType);
    const matchesSearch = !searchTerm || 
      assembly.assembly_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assembly.parts.some(p => 
        p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.category && p.category.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    return matchesType && matchesSearch;
  });

  // Filter flat parts
  const filteredFlatParts = flatParts.filter(part => {
    const matchesType = !filterType || part.part_type === filterType;
    const matchesSearch = !searchTerm || 
      part.partcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  // Summary counts
  const totalAssemblies = assemblies.length;
  const totalParts = flatParts.length;
  const partsByType = flatParts.reduce((acc, part) => {
    acc[part.part_type] = (acc[part.part_type] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Parts Master</h1>
          <p className="text-gray-600 mt-1">
            {totalAssemblies} assemblies, {totalParts} total parts
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition"
        >
          + Add Part
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
        <div className="bg-white rounded-lg shadow p-4 text-center border-l-4 border-red-600">
          <p className="text-2xl font-bold text-gray-900">{totalAssemblies}</p>
          <p className="text-xs text-gray-600">Assemblies</p>
        </div>
        {partTypes.map(type => (
          <div key={type} className="bg-white rounded-lg shadow p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{partsByType[type] || 0}</p>
            <p className="text-xs text-gray-600">{type}</p>
          </div>
        ))}
      </div>

      {/* Filters & View Toggle */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by code, part name, category..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Types</option>
              {partTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">View Mode</label>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grouped')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                  viewMode === 'grouped' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📦 Grouped
              </button>
              <button
                onClick={() => setViewMode('flat')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                  viewMode === 'flat' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📋 Flat List
              </button>
            </div>
          </div>
          {viewMode === 'grouped' && (
            <div className="flex items-end gap-2">
              <button
                onClick={expandAll}
                className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Collapse All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Grouped View */}
      {viewMode === 'grouped' && (
        <div className="space-y-4">
          {filteredAssemblies.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              {assemblies.length === 0 
                ? 'No parts added yet. Click "Add Part" to create your first part.'
                : 'No assemblies match your search criteria.'}
            </div>
          ) : (
            filteredAssemblies.map((assembly) => (
              <div key={assembly.assembly_code} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Assembly Header */}
                <div 
                  className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
                  onClick={() => toggleAssembly(assembly.assembly_code)}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-lg">
                      {expandedAssemblies[assembly.assembly_code] ? '▼' : '▶'}
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {assembly.assembly_code}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {assembly.total_parts} part{assembly.total_parts !== 1 ? 's' : ''} in this assembly
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Categories</p>
                      <p className="font-medium text-gray-900 text-sm">
                        {assembly.categories.length > 0 ? assembly.categories.join(', ') : '-'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Types</p>
                      <div className="flex gap-1 flex-wrap">
                        {assembly.part_types.map(type => (
                          <span key={type} className={`px-2 py-0.5 rounded-full text-xs ${
                            type === 'Outsourced' ? 'bg-blue-100 text-blue-800' :
                            type === 'In-house' ? 'bg-green-100 text-green-800' :
                            type === 'Both' ? 'bg-purple-100 text-purple-800' :
                            type === 'Raw Material' ? 'bg-yellow-100 text-yellow-800' :
                            type === 'Consumable' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right min-w-[60px]">
                      <p className="text-sm text-gray-600">Vendors</p>
                      <p className="font-semibold text-gray-900">{assembly.total_vendors}</p>
                    </div>
                  </div>
                </div>

                {/* Parts List (expanded) */}
                {expandedAssemblies[assembly.assembly_code] && (
                  <div className="border-t border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendors</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {assembly.parts.map((part) => (
                          <tr key={part.id} className="hover:bg-gray-50">
                            <td 
                              className="px-6 py-3 text-sm font-medium text-blue-600 cursor-pointer hover:underline"
                              onClick={() => viewPartDetails(part)}
                            >
                              {part.description}
                            </td>
                            <td className="px-6 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                part.part_type === 'Outsourced' ? 'bg-blue-100 text-blue-800' :
                                part.part_type === 'In-house' ? 'bg-green-100 text-green-800' :
                                part.part_type === 'Both' ? 'bg-purple-100 text-purple-800' :
                                part.part_type === 'Raw Material' ? 'bg-yellow-100 text-yellow-800' :
                                part.part_type === 'Consumable' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {part.part_type}
                              </span>
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-700">{part.category || '-'}</td>
                            <td className="px-6 py-3 text-sm text-gray-700">{part.unit}</td>
                            <td className="px-6 py-3 text-sm text-gray-700">{part.vendor_count || 0}</td>
                            <td className="px-6 py-3 text-sm space-x-2">
                              <button
                                onClick={() => viewPartDetails(part)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleDelete(part.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Flat View */}
      {viewMode === 'flat' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendors</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredFlatParts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      {flatParts.length === 0 
                        ? 'No parts added yet. Click "Add Part" to create your first part.'
                        : 'No parts match your search criteria.'}
                    </td>
                  </tr>
                ) : (
                  filteredFlatParts.map((part) => (
                    <tr key={part.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{part.partcode}</td>
                      <td 
                        className="px-6 py-4 text-sm font-medium text-blue-600 cursor-pointer hover:underline"
                        onClick={() => viewPartDetails(part)}
                      >
                        {part.description}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          part.part_type === 'Outsourced' ? 'bg-blue-100 text-blue-800' :
                          part.part_type === 'In-house' ? 'bg-green-100 text-green-800' :
                          part.part_type === 'Both' ? 'bg-purple-100 text-purple-800' :
                          part.part_type === 'Raw Material' ? 'bg-yellow-100 text-yellow-800' :
                          part.part_type === 'Consumable' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {part.part_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{part.category || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{part.unit}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{part.vendor_count || 0}</td>
                      <td className="px-6 py-4 text-sm space-x-2">
                        <button
                          onClick={() => viewPartDetails(part)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDelete(part.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Part Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Add New Part</h2>
                <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item/Assembly Code *</label>
                  <input
                    type="text"
                    value={partForm.partcode}
                    onChange={(e) => setPartForm({ ...partForm, partcode: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    placeholder="e.g., ITEM-001"
                  />
                  <p className="text-xs text-gray-500 mt-1">Multiple parts can share the same item code</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Part Type *</label>
                  <select
                    value={partForm.part_type}
                    onChange={(e) => setPartForm({ ...partForm, part_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    {partTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Part Name/Description *</label>
                <input
                  type="text"
                  value={partForm.description}
                  onChange={(e) => setPartForm({ ...partForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                  placeholder="e.g., Shaft, Bearing, Housing"
                />
                <p className="text-xs text-gray-500 mt-1">Unique name within the item/assembly</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={partForm.category}
                    onChange={(e) => setPartForm({ ...partForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Fabrication"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <input
                    type="text"
                    value={partForm.unit}
                    onChange={(e) => setPartForm({ ...partForm, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="pieces, kg, meters"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HSN/SAC Code *</label>
                  <input
                    type="text"
                    value={partForm.hsn_code}
                    onChange={(e) => setPartForm({ ...partForm, hsn_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., 8708"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={partForm.notes}
                  onChange={(e) => setPartForm({ ...partForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                  placeholder="Additional notes..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Add Part
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Part Details Modal */}
      {showDetailsModal && selectedPart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedPart.description}</h2>
                  <p className="text-gray-600">Assembly: {selectedPart.partcode}</p>
                </div>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Part Info */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Item/Assembly Code</p>
                  <p className="font-semibold text-gray-900">{selectedPart.partcode}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Part Type</p>
                  <p className="font-semibold text-gray-900">{selectedPart.part_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Category</p>
                  <p className="font-semibold text-gray-900">{selectedPart.category || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Unit</p>
                  <p className="font-semibold text-gray-900">{selectedPart.unit}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">HSN Code</p>
                  <p className="font-semibold text-gray-900">{selectedPart.hsn_code || '-'}</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex gap-4">
                  <button
                    onClick={() => setActiveTab('vendors')}
                    className={`pb-3 px-1 text-sm font-medium border-b-2 transition ${
                      activeTab === 'vendors' 
                        ? 'border-red-600 text-red-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    🏭 Vendors ({partVendors.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`pb-3 px-1 text-sm font-medium border-b-2 transition ${
                      activeTab === 'history' 
                        ? 'border-red-600 text-red-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    📈 Price History ({priceHistory.length})
                  </button>
                </div>
              </div>

              {/* Vendors Tab */}
              {activeTab === 'vendors' && (
                <div>
                  {partVendors.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No vendors supply this part yet.</p>
                      <p className="text-sm text-gray-400 mt-1">Add this part to a vendor&apos;s item catalog to see them here.</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current Rate</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last PO</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {partVendors.map((vendor, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">{vendor.vendor_name}</div>
                                {vendor.phone && <div className="text-xs text-gray-500">{vendor.phone}</div>}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {vendor.contact_person || '-'}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  vendor.item_type === 'Job Work' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                }`}>
                                  {vendor.item_type}
                                </span>
                                {vendor.job_work_type && (
                                  <span className="ml-2 text-xs text-gray-500">({vendor.job_work_type})</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="font-semibold text-gray-900">
                                  {vendor.current_price ? `Rs.${vendor.current_price.toFixed(2)}` : '-'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {vendor.last_po ? (
                                  <div>
                                    <div>{vendor.last_po.po_number}</div>
                                    <div className="text-xs text-gray-400">{formatDate(vendor.last_po.po_date)}</div>
                                  </div>
                                ) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Price History Tab */}
              {activeTab === 'history' && (
                <div>
                  {priceHistory.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No price history recorded yet.</p>
                      <p className="text-sm text-gray-400 mt-1">Price changes will be tracked automatically when rates are updated.</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Change</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {priceHistory.map((entry, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {formatDate(entry.effective_date)}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {entry.vendor_name}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="font-semibold text-gray-900">Rs.{entry.price?.toFixed(2)}</span>
                                {entry.previous_price && (
                                  <div className="text-xs text-gray-400">was Rs.{entry.previous_price?.toFixed(2)}</div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {entry.price_change_percent !== null && entry.price_change_percent !== undefined && (
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    entry.price_change_percent > 0 
                                      ? 'bg-red-100 text-red-700' 
                                      : entry.price_change_percent < 0 
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {entry.price_change_percent > 0 ? '+' : ''}{entry.price_change_percent?.toFixed(1)}%
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  entry.source === 'po_creation' ? 'bg-purple-100 text-purple-700' :
                                  entry.source === 'vendor_item' ? 'bg-blue-100 text-blue-700' :
                                  entry.source === 'vendor_item_update' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {entry.source === 'po_creation' ? 'PO' :
                                   entry.source === 'vendor_item' ? 'New Item' :
                                   entry.source === 'vendor_item_update' ? 'Rate Update' :
                                   'Manual'}
                                </span>
                                {entry.source_reference && (
                                  <span className="ml-1 text-xs text-gray-500">{entry.source_reference}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">
                                {entry.notes || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedPart.notes && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
                  <p className="text-gray-700">{selectedPart.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
